import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Modal, Popconfirm, message } from 'antd';
import dayjs from 'dayjs';
import { EMPLOYEE_EQUIPMENT_QUERY_KEY } from '@/components/email-templates/constants';
import useEmployeeNoticesT, {
  errorText,
} from '@/components/email-templates/useEmployeeNoticesT';
import { decideEquipmentRequest } from '@/services/equipmentRequests.service';
import { DATE_FORMATS } from '@/utils/formUtils';
import { answersLine, itemLabel, itemLine } from './equipmentLines';


const roundButton = '!rounded-[50px]';

/**
 * "Čeka odobrenje" on the employee's "Equipment" tile: the employee's own
 * requests waiting for a decision, with Approve / Reject for whoever may
 * decide (the server says so per request: `canDecide`). Rejecting asks for
 * the reason, which the employee then sees.
 *
 * @param {Array<Object>} props.awaiting - Waiting requests, newest first.
 */
export default function EquipmentApprovalStrip({ awaiting = [], employeeId }) {
  const { t, tn } = useEmployeeNoticesT();
  const queryClient = useQueryClient();
  // The request being rejected, and the reason typed.
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState('');

  const decide = useMutation({
    mutationFn: ({ requestId, ...payload }) =>
      decideEquipmentRequest(employeeId, requestId, payload),
    onSuccess: (result) => {
      if (result?.sendFailed) message.warning(result.message);
      else message.success(result?.message || tn('equipmentDecisionSaved'));
      queryClient.invalidateQueries({
        queryKey: [EMPLOYEE_EQUIPMENT_QUERY_KEY, employeeId],
      });
      setRejecting(null);
      setReason('');
    },
    onError: (error) =>
      message.error(errorText(error, tn('equipmentDecisionFailed'))),
  });

  if (!awaiting.length) return null;

  return (
    <section className="grid gap-2 rounded-xl border border-amber-300 bg-amber-50/60 p-3 text-[13px]">
      <h3 className="m-0 text-[11px] font-extrabold uppercase tracking-[0.08em] text-amber-700">
        {tn('equipmentAwaitingTitle', { count: awaiting.length })}
      </h3>
      {awaiting.map((request) => (
        <div
          key={request._id}
          className="grid gap-1.5 rounded-lg border border-amber-200 bg-white p-2.5"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-bold text-zinc-900">
              {dayjs(request.requestedAt).format(DATE_FORMATS.DISPLAY)}
            </span>
            <span className="text-[12px] text-zinc-500">
              {tn('requestedBy', { name: request.requestedByName || '—' })}
            </span>
          </div>
          <ul className="m-0 pl-4">
            {[...request.items, ...request.extras].map((item) => (
              <li key={itemLabel(item)}>{itemLine(item)}</li>
            ))}
          </ul>
          {request.answers?.length > 0 && (
            <p className="m-0 text-zinc-600">{answersLine(request.answers, tn)}</p>
          )}
          {request.note && (
            <p className="m-0 whitespace-pre-line text-zinc-600">
              <span className="font-bold text-zinc-700">{tn('note')}:</span>{' '}
              {request.note}
            </p>
          )}
          {request.canDecide ? (
            <div className="flex flex-wrap gap-2">
              <Popconfirm
                okButtonProps={{ className: roundButton }}
                cancelButtonProps={{ className: roundButton }}
                title={tn('equipmentApproveConfirm')}
                okText={tn('equipmentApprove')}
                cancelText={tn('cancel')}
                onConfirm={() =>
                  decide.mutateAsync({
                    requestId: request._id,
                    decision: 'approve',
                  })
                }
              >
                <Button
                  type="primary"
                  className={roundButton}
                  loading={decide.isPending}
                >
                  {tn('equipmentApprove')}
                </Button>
              </Popconfirm>
              <Button
                danger
                className={roundButton}
                disabled={decide.isPending}
                onClick={() => setRejecting(request)}
              >
                {tn('equipmentReject')}
              </Button>
            </div>
          ) : (
            <span className="text-[12px] text-zinc-500">
              {tn('equipmentAwaitingOthers')}
            </span>
          )}
        </div>
      ))}

      <Modal
        open={!!rejecting}
        title={tn('equipmentRejectTitle')}
        okText={tn('equipmentReject')}
        cancelText={t('cancel', { ns: 'forms' })}
        okButtonProps={{
          danger: true,
          disabled: !reason.trim(),
          loading: decide.isPending,
          className: roundButton,
        }}
        cancelButtonProps={{ className: roundButton }}
        onCancel={() => {
          setRejecting(null);
          setReason('');
        }}
        onOk={() =>
          decide.mutate({
            requestId: rejecting._id,
            decision: 'reject',
            note: reason.trim(),
          })
        }
        destroyOnHidden
      >
        <p className="m-0 mb-2 text-[12.5px] text-zinc-600">
          {tn('equipmentRejectHint')}
        </p>
        <Input.TextArea
          rows={3}
          maxLength={500}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={tn('equipmentRejectPlaceholder')}
        />
      </Modal>
    </section>
  );
}
